import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, MousePointerClick } from "lucide-react";

const PAD = 8;

export default function TourEngine({ tour, tourId, initialStep = 0, onProgress, onFinish }) {
    const steps = tour.steps;
    const [idx, setIdx] = useState(Math.min(initialStep, steps.length - 1));
    const [rect, setRect] = useState(null);
    const seenRef = useRef(false);
    const missRef = useRef(0);
    const idxRef = useRef(idx);
    idxRef.current = idx;

    const advance = useCallback(() => {
        const next = idxRef.current + 1;
        if (next >= steps.length) {
            onFinish("completed", steps.length);
        } else {
            setIdx(next);
            onProgress(next);
        }
    }, [steps.length, onFinish, onProgress]);

    // Track target element: position, appearance, disappearance, click
    useEffect(() => {
        const step = steps[idx];
        seenRef.current = false;
        missRef.current = 0;
        setRect(null);
        let clickHandler = null;
        let boundEl = null;
        let scrolled = false;

        const tick = () => {
            const el = document.querySelector(step.sel);
            if (el) {
                seenRef.current = true;
                missRef.current = 0;
                if (!scrolled) {
                    scrolled = true;
                    el.scrollIntoView({ block: "center", behavior: "smooth" });
                }
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
                if (step.action === "click" && boundEl !== el) {
                    if (boundEl && clickHandler) boundEl.removeEventListener("click", clickHandler, true);
                    boundEl = el;
                    clickHandler = () => setTimeout(advance, 350);
                    el.addEventListener("click", clickHandler, true);
                }
            } else {
                missRef.current += 1;
                if (seenRef.current && missRef.current > 5) {
                    // target vanished (e.g. clicked & modal closed) → move on
                    advance();
                } else if (!seenRef.current && missRef.current > 30) {
                    // never appeared (~8.5s, tolleranza per tab a caricamento lento) → skip
                    advance();
                }
            }
        };
        tick();
        const iv = setInterval(tick, 280);
        return () => {
            clearInterval(iv);
            if (boundEl && clickHandler) boundEl.removeEventListener("click", clickHandler, true);
        };
    }, [idx, steps, advance]);

    const step = steps[idx];
    if (!step) return null;

    // Tooltip placement: below target, flip above if overflow
    let tipStyle = { opacity: 0 };
    let arrowTop = false;
    if (rect) {
        const tipW = Math.min(360, window.innerWidth - 24);
        const below = rect.top + rect.height + 14;
        const flip = below + 190 > window.innerHeight && rect.top > 210;
        const top = flip ? undefined : below;
        const bottom = flip ? window.innerHeight - rect.top + 14 : undefined;
        let left = rect.left + rect.width / 2 - tipW / 2;
        left = Math.max(12, Math.min(left, window.innerWidth - tipW - 12));
        tipStyle = { top, bottom, left, width: tipW, opacity: 1 };
        arrowTop = !flip;
    }

    return (
        <div data-testid="tour-overlay" className="fixed inset-0 z-[10050] pointer-events-none">
            {/* Spotlight ring + dim */}
            {rect ? (
                <div
                    className="absolute rounded-md vd-spotlight"
                    style={{
                        top: rect.top - PAD, left: rect.left - PAD,
                        width: rect.width + PAD * 2, height: rect.height + PAD * 2,
                        boxShadow: "0 0 0 9999px rgba(5,5,8,0.74), 0 0 24px 2px hsl(var(--gold) / 0.55)",
                        border: "2px solid hsl(var(--gold))",
                        transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
                    }}
                />
            ) : (
                <div className="absolute inset-0 bg-black/60" />
            )}

            {/* Tooltip: visibile solo quando il target esiste */}
            {rect && (
            <div data-testid="tour-tooltip" className="fixed pointer-events-auto vd-tip-in" style={tipStyle}>
                <div className={`w-3 h-3 rotate-45 bg-ink-950 border-gold absolute left-1/2 -ml-1.5 ${arrowTop ? "-top-1.5 border-l-2 border-t-2" : "-bottom-1.5 border-r-2 border-b-2"}`}
                    style={{ borderColor: "hsl(var(--gold))" }} />
                <div className="bg-ink-950 border-2 p-4 shadow-2xl" style={{ borderColor: "hsl(var(--gold))" }}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-gold">{tour.label} · {idx + 1}/{steps.length}</div>
                        <button data-testid="tour-close" onClick={() => onFinish("paused", idx)} className="text-ink-400 hover:text-ink-50 -mt-1"><X size={15} /></button>
                    </div>
                    <div className="font-serif text-lg text-ink-50 mt-1.5">{step.title}</div>
                    <p className="text-xs text-ink-300 mt-1.5 leading-relaxed">{step.text}</p>
                    {step.benefit && <p className="text-[11px] text-gold/90 mt-2">✦ {step.benefit}</p>}
                    <div className="flex items-center gap-2 mt-4">
                        {step.action === "click" ? (
                            <span data-testid="tour-click-hint" className="flex-1 flex items-center gap-1.5 text-[11px] text-gold vd-pulse">
                                <MousePointerClick size={14} /> Clicca sull'elemento evidenziato per continuare
                            </span>
                        ) : (
                            <button data-testid="tour-next" onClick={advance} className="cta-gold px-5 py-2 text-xs">
                                {idx === steps.length - 1 ? "Fine" : "Avanti"}
                            </button>
                        )}
                        <button data-testid="tour-skip" onClick={() => onFinish("dismissed", idx)} className="text-[11px] text-ink-500 hover:text-ink-200 ml-auto">Salta il tour</button>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
